import { Injectable } from '@angular/core'

import { Observable, map } from 'rxjs'

import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import {
  ApiHttpProxyService,
  ForwardProxyMicroservice,
} from '@flowx/designer/shared/api/api-http-proxy.service'
import {
  ApplicationResourceType,
  ProxyApiAction,
} from '@flowx/designer/shared/entities/application.entity'
import {
  Area,
  AreaPlatform,
  NavigationType,
  NewAreaPayload,
  ParentProcessAreaRemovePayload,
  ReorderAreas,
} from '@flowx/designer/shared/entities/nav-area.entity'
import { ApplicationInfo } from '@flowx/designer/shared/model/resource-usage.model'

export type AreasResponse = Map<string, { userTasks: Area[]; navigationTree: Area[] }>

export interface ReorderAreasPayload {
  id: number
  parentNavAreaId: number
  nextSiblingId: number
  previousSiblingId: number
}

export interface PasteAreasPayload {
  sourceNavAreaId: Area['id']
  sourcePlatform: AreaPlatform
  targetNavAreaId: Area['id']
  targetPlatform: AreaPlatform
  swimlaneDefinitionId: SwimlaneSummary['id']
  force?: boolean
}

export interface AreaSummary {
  id: Area['id']
  label: Area['label']
  type: Area['type']
}

export interface AreasFlatRequestParams {
  appId: ApplicationInfo['appId']
  appVersionId: ApplicationInfo['appVersionId']
  resourceId: string
}

export interface NavigationAreaRootOption {
  id: number
  label: string
  type: NavigationType
}

export interface NavigationAreaRootsResponse {
  roots: NavigationAreaRootOption[]
  userTasks: NavigationAreaRootOption[]
}

@Injectable({ providedIn: 'root' })
export class AreasApiService extends ApiHttpProxyService {
  getAreas(processId: number, platform: AreaPlatform): Observable<AreasResponse> {
    return this.get(
      `api/process/navigation`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
      },
      { params: this.addParams({ platform }) }
    ).pipe(map((response) => response.resourceContent))
  }

  getAreasFlat(params: AreasFlatRequestParams): Observable<AreaSummary[]> {
    const { appId, appVersionId, resourceId } = params
    const baseUrl = this.getProxyBaseUrl(appId, appVersionId)

    return this.httpClient
      .get<{
        resourceContent: AreaSummary[]
      }>(`${baseUrl}/rt/process/ri/${resourceId}/admin/api/process/navigation/summary`)
      .pipe(map((response) => response.resourceContent))
  }

  getNavigationAreaRoots(
    areaPlatform: AreaPlatform,
    swimlaneDefinitionId: string
  ): Observable<NavigationAreaRootsResponse> {
    return this.get(
      `api/process/navigation/roots`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
      },
      {
        params: {
          platform: areaPlatform,
          swimlaneId: swimlaneDefinitionId,
        },
      }
    ).pipe(map((response) => response.resourceContent))
  }

  createArea(
    processVersionId: number,
    platform: AreaPlatform,
    payload: NewAreaPayload
  ): Observable<Partial<Area>> {
    return this.patch(
      `api/process/navigation`,
      payload,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        action: ProxyApiAction.CREATE,
      },
      { params: this.addParams({ platform }) }
    ).pipe(map((response) => response.resourceContent))
  }

  createParentArea(
    processVersionId: number,
    platform: AreaPlatform,
    payload: NewAreaPayload
  ): Observable<AreasResponse> {
    return this.patch(
      `api/process/navigation/parent`,
      payload,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        action: ProxyApiAction.CREATE,
      },
      { params: this.addParams({ platform }) }
    ).pipe(map((response) => response.resourceContent))
  }

  pasteAreas(
    processVersionId: ProcessDefinition['id'],
    pastePayload: PasteAreasPayload
  ): Observable<{
    swimlaneDefinitionId: SwimlaneSummary['id']
    userTasks: Area[]
    navigationTree: Area[]
  }> {
    return this.patch(
      `api/process/navigation/paste`,
      pastePayload,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        action: ProxyApiAction.PARTIAL_UPDATE,
      },
      { params: this.addParams({ force: pastePayload.force }) }
    ).pipe(
      map((response) => ({
        swimlaneDefinitionId: pastePayload.swimlaneDefinitionId,
        ...response.resourceContent,
      }))
    )
  }

  updateAreaSettings(payload: {
    id: number
    label: string
    navigationFlowIds: number[]
  }): Observable<Partial<Area>> {
    return this.patch(`api/process/navigation/settings`, payload, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      action: ProxyApiAction.PARTIAL_UPDATE,
    }).pipe(map((response) => response.resourceContent))
  }

  reorderAreas(
    processVersionId: ProcessDefinition['id'],
    platform: AreaPlatform,
    payload: ReorderAreas
  ): Observable<ReorderAreas> {
    return this.patch(
      `api/process/navigation/reorder`,
      {
        id: payload.areaId,
        parentNavAreaId: payload?.newParentId,
        nextSiblingId: payload?.nextSiblingId,
        previousSiblingId: payload?.previousSiblingId,
      },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        action: ProxyApiAction.PARTIAL_UPDATE,
      },
      { params: this.addParams({ platform, force: payload.force }) }
    ).pipe(map(() => payload))
  }

  removeArea(area: Area, platform: AreaPlatform): Observable<Area> {
    return this.patch(
      `api/process/navigation/${area.id}`,
      null,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        action: ProxyApiAction.DELETE,
      },
      { params: this.addParams({ platform }) }
    ).pipe(map(() => area))
  }

  removeParentProcessArea(
    payload: ParentProcessAreaRemovePayload,
    processVersionId: ProcessDefinition['id'],
    platform: AreaPlatform
  ): Observable<void> {
    return this.patch(
      `api/process/navigation/parent`,
      null,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.Process,
        action: ProxyApiAction.DELETE,
      },
      { params: this.addParams({ platform, force: payload.force }) }
    ).pipe(map((response) => response.resourceContent))
  }
}
